/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Deep Dive Content
 * 
 * Contains the 5 initial deep-dive explanations for the Interactive Story.
 * Each deep dive is under 60 seconds to read, uses simple language,
 * and maintains an honest, non-sales tone.
 */

import type { DeepDiveContent } from '../components/DeepDive';

export const deepDives: Record<string, DeepDiveContent> = {
  comfortVsHeat: {
    id: 'comfort-vs-heat',
    title: "Why comfort doesn't mean more heat",
    preview: "Explains how modern systems reduce temperature swings.",
    content: (
      <div className="deep-dive-content">
        <p>
          Your home needs the same total amount of heat regardless of which system you choose.
          This is determined by your insulation, windows, and outside temperature—not your boiler.
        </p>
        
        <div className="deep-dive-section">
          <h4>Steady vs. spiky delivery</h4>
          <p>
            Comfort comes from <em>how smoothly</em> that heat is delivered, not from delivering more of it:
          </p>
          <ul>
            <li><strong>Older on/off systems:</strong> Blast heat for a while, then stop completely. This creates temperature swings you can feel.</li>
            <li><strong>Modulating systems:</strong> Adjust their output continuously, maintaining a steadier temperature.</li>
          </ul>
        </div>

        <div className="deep-dive-section">
          <h4>Same fuel, different experience</h4>
          <p>
            Both systems use roughly the same energy over a day. The difference is in the timing and consistency of delivery, not the total amount.
          </p>
        </div>

        <p className="deep-dive-key-point">
          <strong>Key insight:</strong> Better comfort doesn't mean higher bills—it means smoother control.
        </p>
      </div>
    ),
  },

  controlsExplained: {
    id: 'controls-explained',
    title: 'What controls actually change',
    preview: "Shows how control upgrades improve comfort independently.",
    content: (
      <div className="deep-dive-content">
        <p>
          Not all heating improvements require replacing your boiler. Understanding what your controls do helps you make better decisions.
        </p>

        <div className="deep-dive-section">
          <h4>On/off thermostats (basic)</h4>
          <p>
            Traditional thermostats tell your boiler to run at full power or switch off completely. The boiler itself has no idea what temperature you want—it just runs when told.
          </p>
        </div>

        <div className="deep-dive-section">
          <h4>Modulating controls (advanced)</h4>
          <p>
            Modern controls communicate with modulating boilers to adjust heat output dynamically. Instead of "on or off," they say "give me 40% power right now."
          </p>
          <p>
            This enables room-by-room control, weather compensation, and automatic adjustments throughout the day.
          </p>
        </div>

        <div className="deep-dive-section">
          <h4>The honest bit</h4>
          <p>
            If your existing boiler <em>can</em> modulate but your controls are basic, upgrading just the controls can significantly improve comfort without replacing anything else.
          </p>
          <p>
            A new boiler makes sense when:
          </p>
          <ul>
            <li>Your current one is nearing the end of its life</li>
            <li>You need better hot water delivery</li>
            <li>Your existing boiler can't modulate at all</li>
          </ul>
        </div>

        <p className="deep-dive-key-point">
          <strong>Key insight:</strong> Control upgrades alone can deliver real comfort gains, even with an older boiler.
        </p>
      </div>
    ),
  },

  hotWaterBehavior: {
    id: 'hot-water-behavior',
    title: 'How hot water systems behave',
    preview: "Compares stored vs. on-demand hot water delivery.",
    content: (
      <div className="deep-dive-content">
        <p>
          Hot water delivery is often the deciding factor between system types, but it's rarely explained clearly.
        </p>

        <div className="deep-dive-section">
          <h4>Stored hot water (cylinder systems)</h4>
          <p>
            System and heat-only boilers heat water in advance and store it in an insulated cylinder.
          </p>
          <ul>
            <li><strong>Advantages:</strong> Multiple taps at once, consistent pressure, immediate availability</li>
            <li><strong>Trade-offs:</strong> Needs space, some standby heat loss, takes time to refill after heavy use</li>
          </ul>
          <p>
            Think of it like a reservoir—always ready, but it can run low if you drain it faster than it refills.
          </p>
        </div>

        <div className="deep-dive-section">
          <h4>On-demand (combi boilers)</h4>
          <p>
            Combi boilers heat water instantly as you use it—no storage needed.
          </p>
          <ul>
            <li><strong>Advantages:</strong> Compact, no cylinder, unlimited hot water (in theory)</li>
            <li><strong>Trade-offs:</strong> Lower flow rate, struggles with multiple taps, performance depends on mains pressure</li>
          </ul>
          <p>
            Think of it like a water heater on your shower—works perfectly for one thing at a time, but splits its effort if you ask for more.
          </p>
        </div>

        <div className="deep-dive-section">
          <h4>Why recovery matters</h4>
          <p>
            For households with high hot water demand (multiple bathrooms, frequent showers, large baths), stored systems generally offer a better experience—provided you size the cylinder correctly.
          </p>
          <p>
            For smaller properties with modest usage, combis work well and save space.
          </p>
        </div>

        <p className="deep-dive-key-point">
          <strong>Key insight:</strong> Neither is "better"—it depends on how many people use hot water at once in your home.
        </p>
      </div>
    ),
  },

  systemLifespan: {
    id: 'system-lifespan',
    title: 'Why systems wear out differently',
    preview: "Explains how cycling and water quality affect durability.",
    content: (
      <div className="deep-dive-content">
        <p>
          Two identical boilers can last very different lengths of time, depending on how they're used and what stress they're under.
        </p>

        <div className="deep-dive-section">
          <h4>Cycling and mechanical stress</h4>
          <p>
            <strong>Cycling</strong> means turning on and off repeatedly. Every startup puts stress on components—fans, pumps, valves, and igniters.
          </p>
          <ul>
            <li>Oversized systems cycle more frequently (short bursts, frequent stops)</li>
            <li>Better controls reduce cycling by modulating output</li>
            <li>Poor thermostat placement makes cycling worse</li>
          </ul>
        </div>

        <div className="deep-dive-section">
          <h4>Water quality and scale</h4>
          <p>
            Hard water causes limescale buildup in heat exchangers, reducing efficiency and increasing failure risk. This affects:
          </p>
          <ul>
            <li>Combi boilers (especially on the hot water side)</li>
            <li>System boilers with unprotected cylinders</li>
            <li>Any system without proper water treatment</li>
          </ul>
          <p>
            Regular servicing and water treatment significantly extend lifespan in hard water areas.
          </p>
        </div>

        <div className="deep-dive-section">
          <h4>Same comfort, different durability</h4>
          <p>
            A correctly sized, well-controlled system that runs smoothly will outlast an oversized one that cycles constantly—even if both deliver the same comfort day-to-day.
          </p>
        </div>

        <p className="deep-dive-key-point">
          <strong>Key insight:</strong> Longevity isn't just about quality—it's about matching the system to the load and maintaining it properly.
        </p>
      </div>
    ),
  },

  whenAlternativeSuits: {
    id: 'when-alternative-suits',
    title: 'When another option might suit better',
    preview: "Discusses trade-offs between system types.",
    content: (
      <div className="deep-dive-content">
        <p>
          Our recommendation is based on your specific situation, but no system is universally "best." Here's when you might prefer the alternative.
        </p>

        <div className="deep-dive-section">
          <h4>When a combi might be better (if we recommended stored)</h4>
          <ul>
            <li>You're tight on space and don't have room for a cylinder</li>
            <li>Your hot water usage is genuinely low (1–2 people, infrequent baths)</li>
            <li>You want the simplest possible system with fewer components</li>
            <li>Your mains water pressure is excellent and you rarely use multiple taps at once</li>
          </ul>
        </div>

        <div className="deep-dive-section">
          <h4>When stored hot water might be better (if we recommended combi)</h4>
          <ul>
            <li>You have 3+ people regularly showering in the morning</li>
            <li>You frequently use multiple hot water outlets simultaneously</li>
            <li>Your mains pressure is weak or inconsistent</li>
            <li>You're planning to add bathrooms or increase hot water demand</li>
          </ul>
        </div>

        <div className="deep-dive-section">
          <h4>Cost vs. performance trade-offs</h4>
          <p>
            Combi systems generally cost less upfront but may compromise performance in high-demand scenarios.
            Stored systems cost more initially but handle complex hot water needs better over the long term.
          </p>
        </div>

        <div className="deep-dive-section">
          <h4>You're allowed to disagree</h4>
          <p>
            Our recommendation balances everything we know about your home. But if you have strong preferences—space constraints, budget priorities, or lifestyle factors we haven't captured—those matter too.
          </p>
        </div>

        <p className="deep-dive-key-point">
          <strong>Key insight:</strong> This is decision support, not a mandate. You know your home best.
        </p>
      </div>
    ),
  },
};
